/**
 * The trend of a series: one line per scale question, one point per session (W13, US-LV-23).
 *
 * Read-only. Everything on screen is an aggregate the server already reduced; a session with fewer
 * than five respondents has no point, and the page says how many were held back — never which.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatMean, trendGeometry, type SeriesLine, type SeriesTrend } from "../api.js";
import { getLocale, t } from "../i18n.js";

type Load =
  | { state: "loading" }
  | { state: "error" }
  | { state: "ready"; trend: SeriesTrend };

const W = 320;
const H = 80;

export function Series({ seriesId }: { seriesId: string }): React.JSX.Element {
  const [load, setLoad] = useState<Load>({ state: "loading" });

  useEffect(() => {
    let cancelled = false;
    void api.seriesTrend(seriesId).then(({ data }) => {
      if (cancelled) return;
      setLoad(data ? { state: "ready", trend: data } : { state: "error" });
    });
    return () => {
      cancelled = true;
    };
  }, [seriesId]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10">
      <header className="mb-8 flex flex-wrap items-baseline justify-between gap-4">
        <h1 className="font-display text-display-md">{t("series.title", { id: seriesId })}</h1>
        <Link to="/host" className="no-print text-sm underline">
          {t("results.back")}
        </Link>
      </header>
      {load.state === "loading" && <p>{t("series.loading")}</p>}
      {load.state === "error" && <p>{t("series.unavailable")}</p>}
      {load.state === "ready" && <Body trend={load.trend} />}
    </main>
  );
}

function Body({ trend }: { trend: SeriesTrend }): React.JSX.Element {
  return (
    <>
      {trend.lines.length === 0 && <p>{t("series.empty")}</p>}
      <div className="space-y-8">
        {trend.lines.map((line) => (
          <LineChart key={line.key} line={line} />
        ))}
      </div>
      {trend.withheldPoints > 0 && (
        <p className="mt-6 text-sm opacity-70">{t("series.withheld", { n: String(trend.withheldPoints) })}</p>
      )}
      <p className="mt-2 text-xs opacity-60">{t("series.minRespondents")}</p>
    </>
  );
}

function LineChart({ line }: { line: SeriesLine }): React.JSX.Element {
  const locale = getLocale();
  const pts = trendGeometry(
    line.points.map((p) => p.mean),
    W,
    H,
  );
  return (
    <section>
      <h2 className="mb-2 font-bold">{line.prompt}</h2>
      {line.points.length < 2 && <p className="mb-2 text-sm opacity-70">{t("series.needTwo")}</p>}
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={t("series.chartLabel", { prompt: line.prompt })} className="w-full max-w-md">
        {pts.length > 1 && (
          <polyline fill="none" stroke="currentColor" strokeWidth={2} points={pts.map((p) => `${p.x},${p.y}`).join(" ")} />
        )}
        {pts.map((p, i) => (
          <circle key={line.points[i]!.sessionId} cx={p.x} cy={p.y} r={4} fill="currentColor" />
        ))}
      </svg>
      {/* The table is the accessible reading of the chart, and the only place the exact numbers live. */}
      <table className="mt-2 w-full text-sm">
        <thead>
          <tr className="text-left opacity-70">
            <th scope="col">{t("series.col.date")}</th>
            <th scope="col">{t("series.col.mean")}</th>
            <th scope="col">{t("series.col.respondents")}</th>
          </tr>
        </thead>
        <tbody>
          {line.points.map((p) => (
            <tr key={p.sessionId}>
              <td>{new Date(p.closedAt * 1000).toLocaleDateString(locale)}</td>
              <td>{formatMean(p.mean, locale)}</td>
              <td>{p.respondents}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function SeriesRoute(): React.JSX.Element {
  const { seriesId } = useParams();
  return <Series seriesId={seriesId ?? ""} />;
}
